import { useCallback, useEffect, useState } from 'react';
import { ALL_TAUGHT_KEYS } from '../data/curriculum';
import {
  STORY3_EXPERTS,
  story3ExpertDef,
} from '../data/story3/experts';
import {
  STORY3_MIN_CHARS,
  STORY3_MODES,
  story3ModeDef,
} from '../data/story3/modes';
import { pickStory3Starter } from '../data/story3/prompts';
import { fetchStory3Feedback } from '../lib/story3Api';
import {
  buildNotebookEntry,
  deleteNotebookEntry,
  notebookExcerpt,
  upsertNotebookEntry,
} from '../lib/story3Notebook';
import {
  isStory3CustomExpertValid,
  normalizeStory3CustomExpert,
  STORY3_CUSTOM_EXPERT_MAX,
  STORY3_CUSTOM_EXPERT_MIN,
} from '../lib/story3CustomExpert';
import { useStory3DraftInput } from '../lib/useStory3DraftInput';
import type {
  KeyboardLayoutId,
  Story3ExpertId,
  Story3FeedbackModeId,
  Story3NotebookEntry,
  UserPreferencesPatch,
} from '../types';
import Keyboard from './Keyboard';

type Phase =
  | 'mode'
  | 'notebook'
  | 'expert'
  | 'writing'
  | 'loading'
  | 'feedback'
  | 'error';

function sessionLabel(
  mode: Story3FeedbackModeId,
  expert: Story3ExpertId | null,
  customExpert: string | null,
): string {
  if (mode === 'expert') {
    if (customExpert) return customExpert;
    if (expert) return story3ExpertDef(expert).title;
  }
  return story3ModeDef(mode).title;
}

function hasExpertChoice(
  expert: Story3ExpertId | null,
  customExpert: string | null,
): boolean {
  return expert !== null || customExpert !== null;
}

interface Story3ScreenProps {
  keyboardLayout: KeyboardLayoutId;
  promptSeed?: number;
  initialMode?: Story3FeedbackModeId;
  initialExpert?: Story3ExpertId;
  initialCustomExpert?: string;
  initialNotebook?: Story3NotebookEntry[];
  onPreferencesChange?: (patch: UserPreferencesPatch) => void;
  onFinish: () => void;
  onCancel: () => void;
}

export default function Story3Screen({
  keyboardLayout,
  promptSeed,
  initialMode,
  initialExpert,
  initialCustomExpert,
  initialNotebook,
  onPreferencesChange,
  onFinish,
  onCancel,
}: Story3ScreenProps) {
  const [notebook, setNotebook] = useState<Story3NotebookEntry[]>(
    () => initialNotebook ?? [],
  );
  const [activeNotebookId, setActiveNotebookId] = useState<string | null>(
    null,
  );
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>('mode');
  const [mode, setMode] = useState<Story3FeedbackModeId | null>(
    initialMode ?? null,
  );
  const [expert, setExpert] = useState<Story3ExpertId | null>(
    initialCustomExpert ? null : (initialExpert ?? null),
  );
  const [customExpert, setCustomExpert] = useState<string | null>(
    initialCustomExpert
      ? (normalizeStory3CustomExpert(initialCustomExpert) ??
          initialCustomExpert.slice(0, STORY3_CUSTOM_EXPERT_MAX))
      : null,
  );
  const [starter] = useState(() => pickStory3Starter(promptSeed));
  const [writingKey, setWritingKey] = useState(0);
  const [writingInitial, setWritingInitial] = useState('');
  const [continuationHint, setContinuationHint] = useState<string | null>(
    null,
  );
  const [savedStory, setSavedStory] = useState('');
  const [feedbackBody, setFeedbackBody] = useState('');
  const [feedbackApiMode, setFeedbackApiMode] = useState<'live' | 'mock' | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotebook(initialNotebook ?? []);
  }, [initialNotebook]);

  const saveToNotebook = useCallback(
    (text: string, lastFeedback?: string | null) => {
      if (!onPreferencesChange || !text.trim() || !mode) return;
      const existing = activeNotebookId
        ? notebook.find((e) => e.id === activeNotebookId)
        : undefined;
      const built = buildNotebookEntry({
        id: activeNotebookId ?? undefined,
        title: existing?.title,
        text,
        mode,
        expert,
        customExpert,
        lastFeedback: lastFeedback ?? existing?.lastFeedback ?? null,
        savedAt: existing?.savedAt,
      });
      if (!built) return;
      const next = upsertNotebookEntry(notebook, built);
      setNotebook(next);
      setActiveNotebookId(built.id);
      onPreferencesChange({ story3Notebook: next });
      setSaveNotice('Gemt i notesbog');
      window.setTimeout(() => setSaveNotice(null), 2500);
    },
    [
      activeNotebookId,
      notebook,
      mode,
      expert,
      customExpert,
      onPreferencesChange,
    ],
  );

  const applyNotebookEntry = useCallback(
    (entry: Story3NotebookEntry) => {
      const entryMode = entry.mode ?? initialMode ?? null;
      if (!entryMode) {
        setActiveNotebookId(entry.id);
        setWritingInitial(entry.text);
        setSavedStory(entry.text);
        setPhase('mode');
        return;
      }
      setActiveNotebookId(entry.id);
      setMode(entryMode);
      if (entry.customExpert) {
        setCustomExpert(
          normalizeStory3CustomExpert(entry.customExpert) ??
            entry.customExpert.slice(0, STORY3_CUSTOM_EXPERT_MAX),
        );
        setExpert(null);
      } else if (entry.expert) {
        setExpert(entry.expert);
        setCustomExpert(null);
      } else {
        setExpert(null);
        setCustomExpert(null);
      }
      setWritingInitial(entry.text);
      setSavedStory(entry.text);
      setContinuationHint(
        entryMode === 'question' && entry.lastFeedback
          ? entry.lastFeedback
          : null,
      );
      setWritingKey((k) => k + 1);
      if (
        entryMode === 'expert' &&
        !entry.expert &&
        !entry.customExpert
      ) {
        setPhase('expert');
      } else {
        setPhase('writing');
      }
    },
    [initialMode],
  );

  const handleDeleteNotebookEntry = useCallback(
    (id: string) => {
      const next = deleteNotebookEntry(notebook, id);
      setNotebook(next);
      onPreferencesChange?.({
        story3Notebook: next.length > 0 ? next : [],
      });
      if (activeNotebookId === id) setActiveNotebookId(null);
    },
    [notebook, activeNotebookId, onPreferencesChange],
  );

  const handlePickMode = useCallback(
    (picked: Story3FeedbackModeId) => {
      setMode(picked);
      setActiveNotebookId(null);
      onPreferencesChange?.({ lastStory3FeedbackMode: picked });
      setContinuationHint(null);
      setWritingInitial('');
      setWritingKey((k) => k + 1);
      if (picked === 'expert') {
        setPhase('expert');
      } else {
        setExpert(null);
        setCustomExpert(null);
        setPhase('writing');
      }
    },
    [onPreferencesChange],
  );

  const handlePickExpert = useCallback(
    (picked: Story3ExpertId) => {
      setExpert(picked);
      setCustomExpert(null);
      onPreferencesChange?.({
        lastStory3FeedbackMode: 'expert',
        lastStory3ExpertId: picked,
        lastStory3CustomExpert: undefined,
      });
      setContinuationHint(null);
      setWritingKey((k) => k + 1);
      setPhase('writing');
    },
    [onPreferencesChange],
  );

  const handlePickCustomExpert = useCallback(
    (label: string) => {
      const normalized = normalizeStory3CustomExpert(label);
      if (!normalized) return;
      setCustomExpert(normalized);
      setExpert(null);
      onPreferencesChange?.({
        lastStory3FeedbackMode: 'expert',
        lastStory3CustomExpert: normalized,
        lastStory3ExpertId: undefined,
      });
      setContinuationHint(null);
      setWritingKey((k) => k + 1);
      setPhase('writing');
    },
    [onPreferencesChange],
  );

  const requestFeedback = useCallback(
    async (
      text: string,
      feedbackMode: Story3FeedbackModeId,
      feedbackExpert: Story3ExpertId | null,
      feedbackCustomExpert: string | null,
    ) => {
      setSavedStory(text);
      setPhase('loading');
      setError(null);
      try {
        const result = await fetchStory3Feedback({
          mode: feedbackMode,
          ...(feedbackMode === 'expert' && feedbackCustomExpert
            ? { customExpert: feedbackCustomExpert }
            : feedbackMode === 'expert' && feedbackExpert
              ? { expert: feedbackExpert }
              : {}),
          text,
        });
        setFeedbackBody(result.body);
        setFeedbackApiMode(result.apiMode);
        if (result.expert) {
          setExpert(result.expert);
          setCustomExpert(null);
        }
        if (result.customExpert) {
          setCustomExpert(result.customExpert);
          setExpert(null);
        }
        setPhase('feedback');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kunne ikke hente feedback.');
        setPhase('error');
      }
    },
    [],
  );

  const handleContinueWriting = useCallback(() => {
    if (!mode) return;
    setContinuationHint(mode === 'question' ? feedbackBody : null);
    setWritingInitial(savedStory);
    setWritingKey((k) => k + 1);
    setPhase('writing');
  }, [mode, feedbackBody, savedStory]);

  const handlePickAnotherExpert = useCallback(() => {
    setWritingInitial(savedStory);
    setWritingKey((k) => k + 1);
    setPhase('expert');
  }, [savedStory]);

  const handleNewStory = useCallback(() => {
    setActiveNotebookId(null);
    setMode(null);
    setExpert(null);
    setCustomExpert(null);
    setContinuationHint(null);
    setWritingInitial('');
    setSavedStory('');
    setFeedbackBody('');
    setFeedbackApiMode(null);
    setError(null);
    setWritingKey((k) => k + 1);
    setPhase('mode');
  }, []);

  if (phase === 'mode') {
    return (
      <ModePicker
        onPick={handlePickMode}
        onOpenNotebook={() => setPhase('notebook')}
        notebookCount={notebook.length}
        onCancel={onCancel}
        initialHighlight={initialMode}
      />
    );
  }

  if (phase === 'notebook') {
    return (
      <NotebookView
        entries={notebook}
        onOpen={applyNotebookEntry}
        onDelete={handleDeleteNotebookEntry}
        onNew={() => {
          setActiveNotebookId(null);
          setPhase('mode');
        }}
        onBack={() => setPhase('mode')}
        onCancel={onCancel}
      />
    );
  }

  if (phase === 'expert') {
    return (
      <ExpertPicker
        onPick={handlePickExpert}
        onPickCustom={handlePickCustomExpert}
        onBack={() => setPhase('mode')}
        onCancel={onCancel}
        initialHighlight={initialExpert ?? expert ?? undefined}
        initialCustomExpert={initialCustomExpert ?? customExpert ?? undefined}
      />
    );
  }

  if (phase === 'writing' && mode) {
    if (mode === 'expert' && !hasExpertChoice(expert, customExpert)) {
      return (
        <ExpertPicker
          onPick={handlePickExpert}
          onPickCustom={handlePickCustomExpert}
          onBack={() => setPhase('mode')}
          onCancel={onCancel}
          initialHighlight={initialExpert ?? undefined}
          initialCustomExpert={initialCustomExpert ?? undefined}
        />
      );
    }
    return (
      <WritingView
        key={writingKey}
        mode={mode}
        expert={expert}
        customExpert={customExpert}
        keyboardLayout={keyboardLayout}
        initialText={writingInitial}
        starter={starter}
        continuationHint={continuationHint}
        inNotebook={activeNotebookId !== null}
        saveNotice={saveNotice}
        onBack={() => (mode === 'expert' ? setPhase('expert') : setPhase('mode'))}
        onCancel={onCancel}
        onSave={(text) => saveToNotebook(text)}
        onSubmit={(text) => void requestFeedback(text, mode, expert, customExpert)}
      />
    );
  }

  if (phase === 'loading' && mode) {
    return (
      <LoadingView
        modeLabel={sessionLabel(mode, expert, customExpert)}
        onCancel={onCancel}
      />
    );
  }

  if (phase === 'error') {
    return (
      <ErrorView
        message={error ?? 'Noget gik galt.'}
        onRetry={() => {
          if (savedStory) {
            setWritingInitial(savedStory);
            setWritingKey((k) => k + 1);
            setPhase('writing');
          } else {
            setPhase('mode');
          }
        }}
        onCancel={onCancel}
      />
    );
  }

  if (phase === 'feedback' && mode) {
    return (
      <FeedbackView
        mode={mode}
        expert={expert}
        customExpert={customExpert}
        storyText={savedStory}
        body={feedbackBody}
        apiMode={feedbackApiMode}
        inNotebook={activeNotebookId !== null}
        saveNotice={saveNotice}
        onSave={() => saveToNotebook(savedStory, feedbackBody)}
        onContinue={handleContinueWriting}
        onAnotherExpert={
          mode === 'expert' ? handlePickAnotherExpert : undefined
        }
        onNewStory={handleNewStory}
        onDashboard={onFinish}
      />
    );
  }

  return null;
}

function NotebookView({
  entries,
  onOpen,
  onDelete,
  onNew,
  onBack,
  onCancel,
}: {
  entries: Story3NotebookEntry[];
  onOpen: (entry: Story3NotebookEntry) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onBack: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Fortælling 3 · Notesbog</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Gemte tekster
        </h1>
        <p className="mt-2 text-sm text-muted">
          Vælg en tekst at fortsætte med — eller start en ny blank side.
        </p>
      </header>

      {entries.length === 0 ? (
        <p className="text-sm text-muted">
          Notesbogen er tom. Skriv en fortælling og tryk «Gem i notesbog» når du
          vil beholde den.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {entries.map((entry) => {
            const when = new Date(entry.updatedAt);
            const whenLabel = Number.isNaN(when.getTime())
              ? ''
              : when.toLocaleDateString('da-DK', {
                  day: 'numeric',
                  month: 'short',
                });
            return (
              <li
                key={entry.id}
                className="surface flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between"
              >
                <button
                  type="button"
                  onClick={() => onOpen(entry)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="text-sm font-semibold text-strong">
                    {entry.title}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {notebookExcerpt(entry.text, 100)}
                  </p>
                  {whenLabel && (
                    <p className="mt-1 text-xs text-faint">{whenLabel}</p>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(entry.id)}
                  className="shrink-0 text-xs text-red-600 hover:underline dark:text-red-400"
                >
                  Slet
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onNew}
          className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400"
        >
          Ny blank side
        </button>
        <button type="button" onClick={onBack} className="btn-secondary w-full">
          Tilbage
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary w-full">
          Tilbage til oversigten
        </button>
      </div>
    </div>
  );
}

function ExpertPicker({
  onPick,
  onPickCustom,
  onBack,
  onCancel,
  initialHighlight,
  initialCustomExpert,
}: {
  onPick: (expert: Story3ExpertId) => void;
  onPickCustom: (label: string) => void;
  onBack: () => void;
  onCancel: () => void;
  initialHighlight?: Story3ExpertId;
  initialCustomExpert?: string;
}) {
  const [showCustom, setShowCustom] = useState(
    Boolean(initialCustomExpert && !initialHighlight),
  );
  const [customDraft, setCustomDraft] = useState(initialCustomExpert ?? '');
  const customValid = isStory3CustomExpertValid(customDraft);

  if (showCustom) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
        <header>
          <p className="label">Fortælling 3 · Fagperson</p>
          <h1 className="mt-1 text-2xl font-semibold text-strong">
            Skriv din egen fagperson
          </h1>
          <p className="mt-2 text-sm text-muted">
            Fx astronaut, bedstemor, drageekspert — {STORY3_CUSTOM_EXPERT_MIN}–
            {STORY3_CUSTOM_EXPERT_MAX} tegn.
          </p>
        </header>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-faint">
            Fagperson
          </span>
          <input
            type="text"
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            maxLength={STORY3_CUSTOM_EXPERT_MAX}
            placeholder="Fx zookeeper"
            className="rounded-xl bg-zinc-50 px-4 py-3 text-base text-strong ring-1 ring-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:bg-zinc-900/60 dark:ring-white/10"
            autoFocus
          />
        </label>
        {!customValid && customDraft.trim().length > 0 && (
          <p className="text-xs text-faint">
            Skriv mindst {STORY3_CUSTOM_EXPERT_MIN} bogstaver eller tal.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowCustom(false)}
            className="btn-secondary"
          >
            Tilbage til listen
          </button>
          <button
            type="button"
            disabled={!customValid}
            onClick={() => onPickCustom(customDraft)}
            className="ml-auto rounded-xl bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Fortsæt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Fortælling 3 · Fagperson</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Hvem skal læse din tekst?
        </h1>
        <p className="mt-2 text-sm text-muted">
          Hver rolle læser teksten på sin måde — eller skriv din egen fagperson.
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        {STORY3_EXPERTS.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => onPick(e.id)}
            className={`flex flex-col gap-1 rounded-xl px-4 py-4 text-left ring-1 transition hover:bg-zinc-100 dark:hover:bg-zinc-900 ${
              initialHighlight === e.id
                ? 'bg-zinc-100 ring-zinc-400 dark:bg-zinc-900 dark:ring-zinc-500'
                : 'bg-zinc-50 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5'
            }`}
          >
            <span className="text-sm font-semibold text-strong">{e.title}</span>
            <span className="text-xs text-muted">{e.description}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setCustomDraft(initialCustomExpert ?? '');
            setShowCustom(true);
          }}
          className={`flex flex-col gap-1 rounded-xl px-4 py-4 text-left ring-1 ring-dashed transition hover:bg-zinc-100 dark:hover:bg-zinc-900 sm:col-span-2 ${
            initialCustomExpert
              ? 'bg-zinc-100 ring-zinc-400 dark:bg-zinc-900 dark:ring-zinc-500'
              : 'bg-zinc-50 ring-zinc-300 dark:bg-zinc-900/60 dark:ring-white/10'
          }`}
        >
          <span className="text-sm font-semibold text-strong">
            Skriv din egen fagperson
          </span>
          <span className="text-xs text-muted">
            {initialCustomExpert
              ? `Sidst: ${initialCustomExpert}`
              : 'Navn eller rolle du selv vælger'}
          </span>
        </button>
      </div>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onBack} className="btn-secondary">
          Tilbage til feedback-typer
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
      </div>
    </div>
  );
}

function ModePicker({
  onPick,
  onOpenNotebook,
  notebookCount,
  onCancel,
  initialHighlight,
}: {
  onPick: (mode: Story3FeedbackModeId) => void;
  onOpenNotebook: () => void;
  notebookCount: number;
  onCancel: () => void;
  initialHighlight?: Story3FeedbackModeId;
}) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Fortælling 3 · Eksperiment</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Ny fortælling
        </h1>
        <p className="mt-2 text-sm text-muted">
          Vælg feedback-type og skriv på en blank side. Gem tekster i notesbogen
          når du selv vil.
        </p>
      </header>
      <div className="grid gap-3">
        {STORY3_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onPick(m.id)}
            className={`flex flex-col gap-1 rounded-xl px-4 py-4 text-left ring-1 transition hover:bg-zinc-100 dark:hover:bg-zinc-900 ${
              initialHighlight === m.id
                ? 'bg-zinc-100 ring-zinc-400 dark:bg-zinc-900 dark:ring-zinc-500'
                : 'bg-zinc-50 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5'
            }`}
          >
            <span className="text-sm font-semibold text-strong">{m.title}</span>
            <span className="text-xs text-muted">{m.description}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onOpenNotebook}
          className="w-full rounded-xl bg-zinc-200 px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
        >
          Notesbog{notebookCount > 0 ? ` (${notebookCount})` : ''}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary w-full">
          Annuller
        </button>
      </div>
    </div>
  );
}

function WritingView({
  mode,
  expert,
  customExpert,
  keyboardLayout,
  initialText,
  starter,
  continuationHint,
  inNotebook,
  saveNotice,
  onBack,
  onCancel,
  onSave,
  onSubmit,
}: {
  mode: Story3FeedbackModeId;
  expert: Story3ExpertId | null;
  customExpert: string | null;
  keyboardLayout: KeyboardLayoutId;
  initialText: string;
  starter: ReturnType<typeof pickStory3Starter>;
  continuationHint: string | null;
  inNotebook: boolean;
  saveNotice: string | null;
  onBack: () => void;
  onCancel: () => void;
  onSave: (text: string) => void;
  onSubmit: (text: string) => void;
}) {
  const label = sessionLabel(mode, expert, customExpert);
  const input = useStory3DraftInput(true, initialText);
  const charCount = input.text.trim().length;
  const canFinish = charCount >= STORY3_MIN_CHARS;
  const canSave = charCount > 0;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header>
        <p className="label">Fortælling 3 · {label}</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          {continuationHint ? 'Skriv videre' : 'Skriv din fortælling'}
        </h1>
        {inNotebook && (
          <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
            I notesbogen — tryk Gem for at opdatere
          </p>
        )}
        {saveNotice && (
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
            {saveNotice}
          </p>
        )}
        {!continuationHint && starter && (
          <p className="mt-2 text-sm text-muted">
            Idé til start: {starter.text}
          </p>
        )}
        <p className="mt-1 text-xs text-faint">
          Enter = ny linje · mindst {STORY3_MIN_CHARS} tegn før du er færdig
        </p>
      </header>

      {continuationHint && (
        <section className="surface border-l-4 border-indigo-400 p-4 dark:border-indigo-500">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
            Spørgsmål til dig
          </p>
          <FeedbackBody text={continuationHint} />
        </section>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Tegn" value={String(charCount)} />
        <Stat label="WPM" value={input.stats.wpm.toFixed(0)} />
        <Stat
          label="Flyd"
          value={`${(input.stats.fluency * 100).toFixed(0)}%`}
        />
      </div>

      <div
        className="min-h-[10rem] rounded-xl bg-zinc-50 p-4 text-lg leading-relaxed ring-1 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5"
        aria-live="polite"
      >
        {input.text.length === 0 ? (
          <span className="text-faint">Begynd at skrive…</span>
        ) : (
          <span className="whitespace-pre-wrap text-strong">{input.text}</span>
        )}
      </div>

      <Keyboard
        layoutId={keyboardLayout}
        unlockedKeys={ALL_TAUGHT_KEYS}
        hintLevel="none"
        showFingerDot={false}
      />

      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onBack} className="btn-secondary">
          {mode === 'expert' ? 'Skift fagperson' : 'Skift feedback-type'}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSave(input.text)}
          className="btn-secondary"
        >
          Gem i notesbog
        </button>
        <button
          type="button"
          disabled={!canFinish}
          onClick={() => onSubmit(input.text.trim())}
          className="ml-auto rounded-xl bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Jeg er færdig
        </button>
      </div>
      {!canFinish && charCount > 0 && (
        <p className="text-xs text-faint">
          Skriv {STORY3_MIN_CHARS - charCount} tegn mere.
        </p>
      )}
    </div>
  );
}

function FeedbackView({
  mode,
  expert,
  customExpert,
  storyText,
  body,
  apiMode,
  inNotebook,
  saveNotice,
  onSave,
  onContinue,
  onAnotherExpert,
  onNewStory,
  onDashboard,
}: {
  mode: Story3FeedbackModeId;
  expert: Story3ExpertId | null;
  customExpert: string | null;
  storyText: string;
  body: string;
  apiMode: 'live' | 'mock' | null;
  inNotebook: boolean;
  saveNotice: string | null;
  onSave: () => void;
  onContinue: () => void;
  onAnotherExpert?: () => void;
  onNewStory: () => void;
  onDashboard: () => void;
}) {
  const label = sessionLabel(mode, expert, customExpert);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Fortælling 3 · {label}</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Feedback</h1>
        {inNotebook && (
          <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
            I notesbogen — tryk Gem for at opdatere
          </p>
        )}
        {saveNotice && (
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
            {saveNotice}
          </p>
        )}
        {apiMode === 'mock' && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            Demo-tilstand — forudsigelige svar uden OpenAI-nøgle.
          </p>
        )}
      </header>

      <section className="surface space-y-3 p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-faint">
          Din fortælling
        </p>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted">
          {storyText}
        </p>
      </section>

      <section className="surface space-y-3 p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          {label}
        </p>
        <FeedbackBody text={body} />
      </section>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onSave}
          className="w-full rounded-xl bg-zinc-200 px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
        >
          Gem i notesbog
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400"
        >
          Skriv videre
        </button>
        {onAnotherExpert && (
          <button
            type="button"
            onClick={onAnotherExpert}
            className="w-full rounded-xl bg-zinc-200 px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
          >
            Anden fagperson
          </button>
        )}
        <button
          type="button"
          onClick={onNewStory}
          className="w-full rounded-xl bg-zinc-200 px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
        >
          Ny fortælling
        </button>
        <button
          type="button"
          onClick={onDashboard}
          className="btn-secondary w-full"
        >
          Tilbage til oversigten
        </button>
      </div>
    </div>
  );
}

function FeedbackBody({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-2 text-sm leading-relaxed text-strong">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <br key={i} />;
        if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
          return (
            <p key={i} className="font-semibold">
              {trimmed.slice(2, -2)}
            </p>
          );
        }
        if (trimmed.startsWith('- ')) {
          return (
            <p key={i} className="pl-2 text-muted">
              {trimmed}
            </p>
          );
        }
        return <p key={i}>{trimmed}</p>;
      })}
    </div>
  );
}

function LoadingView({
  modeLabel,
  onCancel,
}: {
  modeLabel: string;
  onCancel: () => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-center gap-6 py-16 text-center">
      <p className="text-sm text-muted">Henter {modeLabel.toLowerCase()}…</p>
      <button type="button" onClick={onCancel} className="btn-secondary">
        Annuller
      </button>
    </div>
  );
}

function ErrorView({
  message,
  onRetry,
  onCancel,
}: {
  message: string;
  onRetry: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 py-8">
      <p className="text-sm text-red-600 dark:text-red-400">{message}</p>
      <div className="flex gap-3">
        <button type="button" onClick={onRetry} className="btn-secondary">
          Prøv igen
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-3 text-center">
      <p className="text-xs text-faint">{label}</p>
      <p className="mt-0.5 font-mono text-lg font-semibold text-strong">
        {value}
      </p>
    </div>
  );
}
